import { useAtomValue } from "jotai";
import { createBrowserRouter, Navigate, type RouteObject } from "react-router-dom";
import { ProtectedRoute } from "../components/ProtectedRoute.js";
import { AppShell } from "../components/AppShell.js";
import { authAtom } from "../lib/auth-store.js";
import { ADMIN_BASE, ADMIN_LOGIN, ADMIN_MODEL_GOVERNANCE, TENANT_BASE, tenantPath } from "../lib/paths.js";
import { Login } from "../pages/Login.js";
import { Dashboard } from "../pages/Dashboard.js";
import { OrganizationList } from "../pages/Organizations/OrganizationList.js";
import { OrganizationDetail } from "../pages/OrganizationDetail/index.js";
import { BasicInfo } from "../pages/OrganizationDetail/BasicInfo.js";
import { GettingStarted } from "../pages/OrganizationDetail/GettingStarted.js";
import { UsersPanel } from "../pages/OrganizationDetail/UsersPanel.js";
import { RolesPanel } from "../pages/OrganizationDetail/RolesPanel.js";
import { LeadershipPanel } from "../pages/OrganizationDetail/LeadershipPanel.js";
import { ProjectAdminsPanel } from "../pages/OrganizationDetail/ProjectAdminsPanel.js";
import { ReviewCenterPanel } from "../pages/OrganizationDetail/ReviewCenterPanel.js";
import { EnterpriseTasksPanel } from "../pages/OrganizationDetail/EnterpriseTasksPanel.js";
import { IntegrationsPanel } from "../pages/OrganizationDetail/IntegrationsPanel.js";
import { ModelManagementPanel } from "../pages/OrganizationDetail/ModelManagementPanel.js";
import { BootstrapPreview } from "../pages/OrganizationDetail/BootstrapPreview.js";
import { AuditPanel } from "../pages/OrganizationDetail/AuditPanel.js";
import { LlmConfigPanel } from "../pages/OrganizationDetail/LlmConfigPanel.js";
import { MigrationPanel } from "../pages/OrganizationDetail/MigrationPanel.js";
import { UsagePanel } from "../pages/OrganizationDetail/UsagePanel.js";
import { RechargePanel } from "../pages/OrganizationDetail/RechargePanel.js";
import { CapabilityCatalogPanel } from "../pages/OrganizationDetail/CapabilityCatalogPanel.js";
import { OrganizationOnboarding } from "../pages/OrganizationOnboarding/index.js";
import { TenantLayout } from "../pages/Tenant/TenantLayout.js";
import { OnboardingApplications } from "../pages/OnboardingApplications.js";
import { PlatformModelGovernance } from "../pages/PlatformModelGovernance.js";
import { PlatformModelCatalogHub } from "../pages/PlatformModelCatalogHub.js";
import { PlatformCapabilityCatalogGate } from "../pages/PlatformCapabilityCatalog.js";
import { SkillHubAdmin } from "../pages/SkillHubAdmin.js";
import { NotFound } from "../pages/NotFound.js";

/**
 * 单域名路径路由（2026-07-04，deploy/dosia-website-domain-cutover.md）：
 * 平台面 = /admin/login + /admin + /admin/organizations/...（super/support）；
 * 企业面 = /org/:orgCode（org-admin 专属，TenantLayout 就地登录，如 /org/catalyst）。
 * 不用 router basename —— 平台与租户是两棵根级路由树；
 * 平台路径统一挂在 ADMIN_BASE 分支下。org-admin 误入平台路径一律弹回自己企业入口；
 * 分流只是 UX，硬门在服务端。
 */
function HomeRedirect(): JSX.Element {
  const auth = useAtomValue(authAtom);
  if (auth?.scope === "org-admin" && auth.orgCode) {
    return <Navigate to={tenantPath(auth.orgCode)} replace />;
  }
  return <Dashboard />;
}

function OrganizationsRedirect(): JSX.Element {
  const auth = useAtomValue(authAtom);
  if (auth?.scope === "org-admin" && auth.orgCode) {
    return <Navigate to={tenantPath(auth.orgCode)} replace />;
  }
  return <OrganizationList />;
}

function OnboardingApplicationsGate(): JSX.Element {
  const auth = useAtomValue(authAtom);
  if (auth?.scope === "org-admin" && auth.orgCode) {
    return <Navigate to={tenantPath(auth.orgCode)} replace />;
  }
  return <OnboardingApplications />;
}

function ModelGovernanceGate(): JSX.Element {
  const auth = useAtomValue(authAtom);
  if (auth?.scope === "org-admin" && auth.orgCode) {
    return <Navigate to={tenantPath(auth.orgCode)} replace />;
  }
  return <PlatformModelGovernance />;
}

function ModelCatalogHubGate(): JSX.Element {
  const auth = useAtomValue(authAtom);
  if (auth?.scope === "org-admin" && auth.orgCode) {
    return <Navigate to={tenantPath(auth.orgCode)} replace />;
  }
  return <PlatformModelCatalogHub />;
}

function SkillHubAdminGate(): JSX.Element {
  const auth = useAtomValue(authAtom);
  if (auth?.scope === "org-admin" && auth.orgCode) {
    return <Navigate to={tenantPath(auth.orgCode)} replace />;
  }
  return <SkillHubAdmin />;
}

function OrgIndexRedirect(): JSX.Element {
  const auth = useAtomValue(authAtom);
  // org-admin 首登落「开始使用」引导页（该页检测核心项全完成时自己放行去基本信息）
  if (auth?.scope === "org-admin") {
    return <Navigate to="getting-started" state={{ fromIndex: true }} replace />;
  }
  return <Navigate to="onboarding" replace />;
}

/** 组织详情子路由 — 平台树（/admin/organizations/:code）与租户树（/:code）共用一份，防漂移。 */
export function orgDetailChildren(): RouteObject[] {
  return [
    { index: true, element: <OrgIndexRedirect /> },
    { path: "getting-started", element: <GettingStarted /> },
    { path: "onboarding", element: <OrganizationOnboarding /> },
    { path: "basic-info", element: <BasicInfo /> },
    { path: "users", element: <UsersPanel /> },
    { path: "roles", element: <RolesPanel /> },
    { path: "leadership", element: <LeadershipPanel /> },
    { path: "project-admins", element: <ProjectAdminsPanel /> },
    // 审批中心（技能/Agent + MCP 两分区）。旧「MCP 审核」路由 redirect 防书签断。
    { path: "reviews", element: <ReviewCenterPanel /> },
    { path: "mcp-review", element: <Navigate to="../reviews" replace /> },
    // 自动化任务中心（dosia-enterprise 服务间通道透传；org-admin + super/support 可管理）。
    { path: "tasks", element: <EnterpriseTasksPanel /> },
    // 集成与凭据（组织级 app 凭证：腾讯文档说明 + 云端执行应用；Tavily/火山密钥卡已随 REQ-268 退役，两腿改走工具模型策展的网关组；org-admin + super/support 可管理）。
    { path: "integrations", element: <IntegrationsPanel /> },
    // 企业模型管理（ByteSpike 接管 / 模型 override / 广播下发 / 成员监控；dosia-enterprise 通道透传；org-admin + super/support 可管理）。
    { path: "models", element: <ModelManagementPanel /> },
    { path: "capabilities", element: <CapabilityCatalogPanel /> },
    // 启动态 tab 已折叠进组织搭建 wizard：保留旧路由 → redirect 到对应步，避免书签 404。
    {
      path: "feishu",
      element: <Navigate to="../onboarding?step=1" replace />,
    },
    {
      path: "dept-tree",
      element: <Navigate to="../onboarding?step=2" replace />,
    },
    {
      path: "role-provisioning",
      element: <Navigate to="../onboarding?step=3" replace />,
    },
    { path: "llm-config", element: <LlmConfigPanel /> },
    { path: "migration", element: <MigrationPanel /> },
    { path: "bootstrap", element: <BootstrapPreview /> },
    { path: "audit", element: <AuditPanel /> },
    { path: "usage", element: <UsagePanel /> },
    { path: "recharge", element: <RechargePanel /> },
  ];
}

export const routes: RouteObject[] = [
  { path: ADMIN_LOGIN, element: <Login /> },
  // 平台面（/admin 分支）
  {
    path: ADMIN_BASE,
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          { index: true, element: <HomeRedirect /> },
          { path: "organizations", element: <OrganizationsRedirect /> },
          // 企业入驻申请（平台级队列，super-only —— 硬门在服务端 requireSuper）。
          //（#910 平台面全挂 /admin 下，orgCode 保留字收敛为 "admin"，
          //  PR-2 兑换 orgCode 校验拒绝之；见 spec §4.1）
          { path: "onboarding", element: <OnboardingApplicationsGate /> },
          // 全量模型/分组/协议统一治理页（super-only —— 硬门在服务端 requireSuper）。
          { path: "model-governance", element: <ModelGovernanceGate /> },
          // 「模型清单」数据枢纽页（P3，super-only —— 硬门在服务端 requireSuper）。
          { path: "model-catalog", element: <ModelCatalogHubGate /> },
          // 平台能力总库（P0C，super/support 只读 —— 硬门在服务端 requireSuper）。
          { path: "capabilities", element: <PlatformCapabilityCatalogGate /> },
          // Skill Hub 平台 Catalog 管理（super-only —— 硬门在服务端 requireSuper）。
          { path: "skill-hub", element: <SkillHubAdminGate /> },
          // 旧书签保留：统一 redirect 到新页。
          { path: "brain-curation", element: <Navigate to={ADMIN_MODEL_GOVERNANCE} replace /> },
          { path: "tool-curation", element: <Navigate to={`${ADMIN_MODEL_GOVERNANCE}?tab=tools`} replace /> },
          { path: "pi-curation", element: <Navigate to={ADMIN_MODEL_GOVERNANCE} replace /> },
          { path: "tier-mapping", element: <Navigate to={ADMIN_MODEL_GOVERNANCE} replace /> },
          {
            path: "organizations/:code",
            element: <OrganizationDetail />,
            children: orgDetailChildren(),
          },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
  // 根路径 → 平台面（域名下 / 归官网，SPA 只在 :8011 裸 IP / 深链兜底时见到）
  { path: "/", element: <Navigate to={ADMIN_BASE} replace /> },
  // 企业租户入口 /org/:orgCode（/org 前缀与官网页面/平台面永久隔离命名空间，免保留字黑名单）
  {
    path: `${TENANT_BASE}/:code`,
    element: <TenantLayout />,
    children: [
      {
        element: <OrganizationDetail />,
        children: orgDetailChildren(),
      },
    ],
  },
  { path: "*", element: <NotFound /> },
];

export const router = createBrowserRouter(routes);
